import { useMemo, useState } from "react";
import { Award, FileText, Heart, Mail, Package, Printer, Receipt, Tag } from "lucide-react";
import { useCustomMutation, useTranslate } from "@refinedev/core";
import { Button } from "@fawkes/ui";

import { AFDRUKKEN, afdrukHtml, drukAf, type AfdrukDossier, type AfdrukSoort } from "./crematoriumAfdrukken";

// Het tabblad Afdrukken (mockup): links de afdrukken die bij deze uitvaart horen, rechts het
// voorbeeld van de gekozen, en één knop Afdrukken. Elke afdruk wordt op het dossier vastgelegd
// als document, zodat op het tabblad Documenten te zien is wat er is gemaakt en door wie.

const ICOON: Record<AfdrukSoort, typeof Award> = {
  FACTUUR: Receipt,
  CREMATIEFACTUUR: Receipt,
  CREMATIECERTIFICAAT: Award,
  HERINNERINGSKAART: Heart,
  BEGELEIDENDE_BRIEF: FileText,
  AS_RETOURBRIEF: Package,
  IDENTIFICATIELABEL: Tag,
  URNLABEL: Tag,
  ADRESETIKET: Mail,
};

export function CrematieDossierAfdrukken({
  id,
  dossier,
  onGemaakt,
}: {
  id: string;
  dossier: AfdrukDossier;
  /** Na het vastleggen van een afdruk als document: het dossier opnieuw laden. */
  onGemaakt: () => void;
}) {
  const t = useTranslate();
  const [gekozen, setGekozen] = useState<AfdrukSoort>(dossier.factuur ? "FACTUUR" : "CREMATIECERTIFICAAT");
  const [melding, setMelding] = useState<"geblokkeerd" | "fout" | null>(null);
  const { mutate, isLoading: bezig } = useCustomMutation();

  const html = useMemo(() => afdrukHtml(gekozen, dossier), [gekozen, dossier]);

  const afdrukken = () => {
    setMelding(null);
    if (!drukAf(html)) {
      // De browser hield het printvenster tegen (pop-upblokkering); dan is er niets afgedrukt.
      setMelding("geblokkeerd");
      return;
    }
    mutate(
      {
        url: `crematorium/dossiers/${id}/documenten`,
        method: "post",
        values: { type: gekozen, omschrijving: t(`crematieDetail.afdruk.${gekozen}`), kanaal: "PRINT" },
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => onGemaakt(), onError: () => setMelding("fout") },
    );
  };

  return (
    <div className="crem-cols crem-cols--aside">
      <div className="crem-card">
        <div className="crem-card__head">
          <h3 className="crem-card__title">{t("crematieDetail.afdruk.titel")}</h3>
          <div className="crem-card__head-actions">
            <Button variant="primary" onClick={afdrukken} disabled={bezig}>
              <Printer size={17} aria-hidden="true" /> {t("crematieDetail.afdruk.afdrukken")}
            </Button>
          </div>
        </div>
        {melding && (
          <p className="fawkes-error" role="alert">{t(`crematieDetail.afdruk.${melding}`)}</p>
        )}
        <div className="crem-list">
          {AFDRUKKEN.map((a) => {
            const Icoon = ICOON[a.soort];
            const beschikbaar = !a.alleenMetFactuur || dossier.factuur != null;
            return (
              <button
                key={a.soort}
                type="button"
                className="crem-list__item"
                aria-pressed={gekozen === a.soort}
                disabled={!beschikbaar}
                onClick={() => setGekozen(a.soort)}
              >
                <span className="crem-list__icon"><Icoon size={18} aria-hidden="true" /></span>
                <span className="crem-list__body">
                  <strong>{t(`crematieDetail.afdruk.${a.soort}`)}</strong>
                  <small>{a.formaat}</small>
                </span>
                <span className={`fui-badge ${beschikbaar ? "fui-badge--success" : "fui-badge--warning"}`}>
                  {t(beschikbaar ? "crematieDetail.afdruk.beschikbaar" : "crematieDetail.afdruk.naUitgifte")}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Het voorbeeld: dezelfde html als de afdruk, in een kader. */}
      <div className="crem-card">
        <div className="crem-card__head">
          <h3 className="crem-card__title">{t("crematieDetail.afdruk.voorbeeld")}</h3>
        </div>
        <iframe
          title={t("crematieDetail.afdruk.voorbeeld")}
          className="crem-afdruk__voorbeeld"
          srcDoc={html}
          data-testid="afdruk-voorbeeld"
        />
      </div>
    </div>
  );
}
